import { type FormEvent, type JSX, useState } from 'react';
import type { Berichtsbeschreibung } from '@crm/kontrakte';

import { ApiFehler } from '../api/fehler';
import {
  type Berichtsentwurf,
  type Erlaeuterungsentwurf,
  type KiKennzeichnung,
  type KiVorschlag,
  berichtVorschlagen,
  berichtsvorschlagEntscheiden,
  erlaeuterungEntscheiden,
  erlaeuterungErzeugen,
} from '../api/ki';
import { Meldung } from '../komponenten/Meldung';

/**
 * DIE KI IM BAUKASTEN (KI-15, Auftrag #54).
 *
 * Zwei Teile, je mit eigenem Schalter unter Umsysteme › KI:
 *
 *   „In eigenen Worten"  eine Frage wird zu einem Bericht des Baukastens;
 *                        Nachfragen verfeinern ihn. Die Zahlen rechnet das
 *                        CRM wie bei jedem Bericht.
 *   „Erläuterung"        ein kurzer Kommentar zum berechneten Bericht, als
 *                        Entwurf zur Prüfung — nie versendet.
 *
 * Beide tragen die Kennzeichnung (KI-01) und werden übernommen, geändert
 * oder verworfen (KI-04). Gespeichert wird ein Bericht nur, wenn man ihn
 * speichert.
 */

const ZEIT = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' });

/** KI-01: dass es von einer KI stammt, wann, von welchem Modell und woraus. */
export function Kikennzeichnung({ kennzeichnung }: { readonly kennzeichnung: KiKennzeichnung }): JSX.Element {
  return (
    <p className="ki-kennzeichnung">
      <span className="marke marke--akzent">KI-Vorschlag</span>
      {kennzeichnung.modell} · {ZEIT.format(new Date(kennzeichnung.erzeugtAm))} · Grundlage: {kennzeichnung.datengrundlage}
    </p>
  );
}

/**
 * „In eigenen Worten": Frage und Nachfrage in einem Feld. Ist ein Vorschlag
 * offen, verfeinert die Eingabe ihn; die aktuelle Beschreibung geht immer
 * als Grundlage mit.
 */
export function InEigenenWorten({
  beschreibung,
  vorschlag,
  beiVorschlag,
  beiEntschieden,
}: {
  /** Was gerade im Baukasten steht — die Grundlage einer Nachfrage. */
  readonly beschreibung: Berichtsbeschreibung;
  readonly vorschlag: KiVorschlag<Berichtsentwurf> | undefined;
  readonly beiVorschlag: (v: KiVorschlag<Berichtsentwurf>) => void;
  readonly beiEntschieden: (v: KiVorschlag<Berichtsentwurf>) => void;
}): JSX.Element {
  const [frage, setzeFrage] = useState('');
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);

  const fragen = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (frage.trim() === '' || laeuft) return;
    setzeLaeuft(true);
    setzeFehler(undefined);
    try {
      // Auftrag CRM-46: Ein neuer Bericht ohne Kennzahl ist keine Grundlage — die Frage baut ihn neu.
      const grundlage = beschreibung.kennzahlen.length === 0 ? null : beschreibung;
      beiVorschlag(await berichtVorschlagen(frage.trim(), grundlage, vorschlag?.id ?? null));
      setzeFrage('');
    } catch (u) {
      if (u instanceof ApiFehler) setzeFehler(u);
      else throw u;
    } finally {
      setzeLaeuft(false);
    }
  };

  const entscheiden = async (art: 'uebernehmen' | 'verwerfen'): Promise<void> => {
    if (vorschlag === undefined) return;
    setzeFehler(undefined);
    try {
      beiEntschieden(
        await berichtsvorschlagEntscheiden(vorschlag.id, art === 'uebernehmen' ? { art, beschreibung } : { art }),
      );
    } catch (u) {
      if (u instanceof ApiFehler) setzeFehler(u);
      else throw u;
    }
  };

  const ohneBericht = vorschlag !== undefined && vorschlag.entwurf.beschreibung === null;
  return (
    <div className="ki-frage">
      <form className="ki-frage__zeile" onSubmit={(e) => void fragen(e)}>
        <label htmlFor="bk-ki-frage">In eigenen Worten</label>
        <input
          id="bk-ki-frage"
          type="text"
          value={frage}
          maxLength={500}
          disabled={laeuft}
          placeholder={
            vorschlag === undefined
              ? 'etwa „Umsatz nach Produktart im zweiten Quartal gegenüber Vorjahr"'
              : 'Nachfrage, etwa „nur Privatkunden", „als Linie", „gegen Vorjahr"'
          }
          onChange={(e) => setzeFrage(e.target.value)}
        />
        <button type="submit" className="knopf" disabled={laeuft || frage.trim() === ''}>
          {laeuft ? 'Wird vorgeschlagen …' : vorschlag === undefined ? 'Bericht vorschlagen' : 'Verfeinern'}
        </button>
      </form>
      {fehler !== undefined && <Meldung fehler={fehler} />}
      {vorschlag !== undefined && (
        <div className="ki-vorschlag" role="status">
          <Kikennzeichnung kennzeichnung={vorschlag.kennzeichnung} />
          {!ohneBericht && <p className="ki-vorschlag__titel">{vorschlag.entwurf.titel}</p>}
          {vorschlag.entwurf.hinweise.length > 0 && (
            <ul className="ki-vorschlag__hinweise" aria-label="Was der Katalog nicht kann">
              {vorschlag.entwurf.hinweise.map((h) => (
                <li key={h}>{h}</li>
              ))}
            </ul>
          )}
          <p className="hinweiszeile">
            {ohneBericht
              ? 'Kein Bericht entstanden; der Baukasten bleibt unverändert.'
              : 'Übernommen erst mit „Übernehmen", gespeichert erst mit „Speichern".'}
          </p>
          <div className="knopfreihe">
            <button type="button" className={ohneBericht ? 'knopf' : 'knopf knopf--eingriff'} onClick={() => void entscheiden('verwerfen')}>
              {ohneBericht ? 'Schließen' : 'Verwerfen'}
            </button>
            {!ohneBericht && (
              <button type="button" className="knopf knopf--haupt" onClick={() => void entscheiden('uebernehmen')}>
                Übernehmen
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * „Erläuterung": ein kurzer Kommentar zum berechneten Bericht. Jede Aussage
 * nennt ihre Kennzahl und Zeile; der Text lässt sich vor dem Übernehmen
 * ändern. Übernommen heißt: in die Zwischenablage — versendet wird nichts.
 */
export function Erlaeuterung({ beschreibung }: { readonly beschreibung: Berichtsbeschreibung }): JSX.Element {
  const [vorschlag, setzeVorschlag] = useState<KiVorschlag<Erlaeuterungsentwurf> | undefined>(undefined);
  const [texte, setzeTexte] = useState<readonly string[]>([]);
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [erledigt, setzeErledigt] = useState<string | undefined>(undefined);

  const erzeugen = async (): Promise<void> => {
    setzeLaeuft(true);
    setzeFehler(undefined);
    setzeErledigt(undefined);
    try {
      const v = await erlaeuterungErzeugen(beschreibung);
      setzeVorschlag(v);
      setzeTexte(v.entwurf.aussagen.map((a) => a.text));
    } catch (u) {
      if (u instanceof ApiFehler) setzeFehler(u);
      else throw u;
    } finally {
      setzeLaeuft(false);
    }
  };

  const entscheiden = async (art: 'uebernehmen' | 'verwerfen'): Promise<void> => {
    if (vorschlag === undefined) return;
    setzeFehler(undefined);
    try {
      if (art === 'verwerfen') {
        await erlaeuterungEntscheiden(vorschlag.id, { art });
        setzeErledigt('Die Erläuterung ist verworfen.');
      } else {
        const aussagen = vorschlag.entwurf.aussagen
          .map((a, i) => ({ text: texte[i] ?? '', kennzahl: a.kennzahl, zeile: a.zeile }))
          .filter((a) => a.text.trim() !== '');
        const v = await erlaeuterungEntscheiden(vorschlag.id, { art, aussagen });
        const text = (v.fassung ?? v.entwurf).aussagen.map((a) => a.text).join('\n');
        try {
          await navigator.clipboard.writeText(text);
          setzeErledigt('Die Erläuterung ist übernommen und in der Zwischenablage. Versendet wird nichts.');
        } catch {
          setzeErledigt('Die Erläuterung ist übernommen. Versendet wird nichts.');
        }
      }
      setzeVorschlag(undefined);
    } catch (u) {
      if (u instanceof ApiFehler) setzeFehler(u);
      else throw u;
    }
  };

  return (
    <div className="ki-erlaeuterung">
      {vorschlag === undefined ? (
        <button type="button" className="knopf" disabled={laeuft} onClick={() => void erzeugen()}>
          {laeuft ? 'Erläuterung entsteht …' : 'Erläuterung'}
        </button>
      ) : (
        <div className="ki-vorschlag">
          <Kikennzeichnung kennzeichnung={vorschlag.kennzeichnung} />
          <p className="hinweiszeile">Entwurf zur Prüfung — wird nie automatisch versendet.</p>
          <ol className="ki-erlaeuterung__aussagen">
            {vorschlag.entwurf.aussagen.map((a, i) => (
              <li key={`${a.kennzahl}-${String(i)}`}>
                <textarea
                  rows={2}
                  value={texte[i] ?? ''}
                  aria-label={`Aussage ${String(i + 1)}`}
                  onChange={(e) => setzeTexte((t) => t.map((x, j) => (j === i ? e.target.value : x)))}
                />
                {/* KI-03: woraus die Aussage stammt */}
                <span className="marke">
                  {a.kennzahlName} · {a.zeile ?? 'Gesamt'}
                </span>
              </li>
            ))}
          </ol>
          <div className="knopfreihe">
            <button type="button" className="knopf knopf--eingriff" onClick={() => void entscheiden('verwerfen')}>
              Verwerfen
            </button>
            <button type="button" className="knopf knopf--haupt" onClick={() => void entscheiden('uebernehmen')}>
              Übernehmen
            </button>
          </div>
        </div>
      )}
      {erledigt !== undefined && (
        <p className="hinweiszeile" role="status">
          {erledigt}
        </p>
      )}
      {fehler !== undefined && <Meldung fehler={fehler} />}
    </div>
  );
}
